<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Modal from "@/ui/components/modal.vue";
import MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;
import { useApiClient } from "@/api/client";
import BexAdministrationDO = Modules.BookingExperts.Data.BexAdministrationDO;
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import { toastApiResult } from "@/toaster";
import { required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";

const api = useApiClient("bookingExperts");

const props = defineProps<{
	modelValue: boolean;
	curve: PriceModelCurveDO;
	pricelistIds: Array<number>;
	administrations: BexAdministrationDO[];
}>();

const emit = defineEmits<{
	(e: "update:modelValue", value: boolean): void;
	(e: "applied", newCurve: PriceModelCurveDO): void;
}>();

const loading = ref(false);
const applying = ref(false);
const selected = ref<Set<number>>(new Set());
const customName = ref("");
const customDescription = ref("");
const customColor = ref("");
const previouslyAppliedPricelists = ref<Record<string, MasterPriceDO[]>>({});

async function fetchNextName(curveName: string): Promise<string> {
	const baseName = curveName.replace(/ \(\d+\)$/, "");
	const maxBaseLength = 124;
	const truncatedBase =
		baseName.length > maxBaseLength
			? baseName.slice(0, maxBaseLength)
			: baseName;

	const result = await api.getPriceModelCurves({
		"filter[name]": truncatedBase,
	});
	if (!result.isOk()) return `${truncatedBase} (1)`;

	const escaped = truncatedBase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const pattern = new RegExp(`^${escaped} \\((\\d+)\\)$`);

	const takenNumbers = result.value
		.map((c) => {
			const match = c.name.match(pattern);
			return match ? parseInt(match[1]) : null;
		})
		.filter((n): n is number => n !== null);

	let n = 1;
	while (takenNumbers.includes(n)) n++;
	return `${truncatedBase} (${n})`;
}

async function fetchPricelists() {
	loading.value = true;
	const [pricelistsResult] = await Promise.all([
		api.getMasterPrices(),
		fetchNextName(props.curve.name).then((name) => (customName.value = name)),
	]);
	if (pricelistsResult.isOk()) {
		const adminMap = new Map(props.administrations.map((a) => [a.id, a.name]));

		previouslyAppliedPricelists.value = Object.fromEntries(
			pricelistsResult.value
				.filter((p) => props.pricelistIds.includes(p.id))
				.reduce((acc, pricelist) => {
					const key =
						adminMap.get(pricelist.administration_id!) ??
						`Administration ${pricelist.administration_id}`;
					if (!acc.has(key)) acc.set(key, []);
					acc.get(key)!.push(pricelist);
					return acc;
				}, new Map<string, MasterPriceDO[]>())
		);
	}
	loading.value = false;
}

async function apply() {
	applying.value = true;

	const duplicateName = customName.value.trim();
	const duplicateDescription = customDescription.value || "";
	const duplicateResult = await toastApiResult(
		api.createPriceModelCurve({
			...props.curve,
			name: duplicateName,
			description: duplicateDescription,
			color: customColor.value,
		})
	);
	if (!duplicateResult.isOk()) {
		applying.value = false;
		return;
	}

	const newCurve = duplicateResult.value;
	const newCurveId = duplicateResult.value.id;
	const originalCurveId = props.curve.id;

	const selectedPricelists = Object.values(previouslyAppliedPricelists.value)
		.flat()
		.filter((p) => selected.value.has(p.id));

	await Promise.all(
		selectedPricelists.map(async (pricelist) => {
			const rangesResult = await api.getRangesForPrice(pricelist.id);
			if (!rangesResult.isOk()) return;

			const updatedRanges = rangesResult.value.map((range) => ({
				id: range.id,
				curveId: range.curveId === originalCurveId ? newCurveId : range.curveId,
				from: range.start_date,
				to: range.end_date,
			}));

			await toastApiResult(
				api.setRanges(pricelist.id, { items: updatedRanges })
			);
		})
	);

	applying.value = false;
	emit("applied", newCurve);
	emit("update:modelValue", false);
}

const isVisible = computed({
	get: () => props.modelValue,
	set: (val) => {
		if (!val && applying.value) return;
		emit("update:modelValue", val);
	},
});

watch(
	() => props.modelValue,
	(val) => {
		if (val) {
			selected.value = new Set();
			customName.value = "";
			customDescription.value = "";
			customColor.value = props.curve.color ?? "";
			fetchPricelists();
		}
	}
);

const getParentCheckboxState = (masterPrices: MasterPriceDO[]) => {
	const isAllActive = masterPrices.every((p) => selected.value.has(p.id));
	const isIndeterminate =
		!isAllActive && masterPrices.some((p) => selected.value.has(p.id));

	return {
		modelValue: isAllActive,
		indeterminate: isIndeterminate,
	};
};

const setCheckboxState = (id: number, value: boolean) => {
	if (value) {
		selected.value.add(id);
	} else {
		selected.value.delete(id);
	}
};

const setParentCheckboxState = (
	masterPrices: MasterPriceDO[],
	value: boolean
) => {
	masterPrices.forEach((p) => {
		setCheckboxState(p.id, value);
	});
};
</script>

<template>
	<Modal
		v-model:visible="isVisible"
		title="Save as new curve"
		icon="fa-light fa-copy"
		width="520px"
	>
		<div class="d-flex align-start ga-3 mb-3">
			<color-picker v-model="customColor" variant="filled" />
			<v-text-field
				v-model="customName"
				label="Name"
				:rules="[required(t)]"
				:disabled="applying"
				hide-details="auto"
			/>
		</div>
		<v-textarea
			v-model="customDescription"
			label="Description"
			hide-details
			max-height="200"
			rows="3"
			:disabled="applying"
			class="mb-5"
		/>

		<p class="text-body-2 text-medium-emphasis mb-4">
			Your changes will be saved as a new curve. Select which pricelists should
			switch over to it.
		</p>
		<div v-if="loading" class="d-flex justify-center py-6">
			<v-progress-circular indeterminate color="primary" />
		</div>
		<v-expansion-panels v-else>
			<v-expansion-panel
				v-for="(pricelists, administrationName) in previouslyAppliedPricelists"
				:key="administrationName"
			>
				<v-expansion-panel-title
					class="px-4 bg-background ga-1"
					style="font-size: 1rem; font-weight: 400"
				>
					<v-checkbox
						v-bind="getParentCheckboxState(pricelists)"
						density="compact"
						class="my-n3"
						hide-details
						@update:model-value="(v) => setParentCheckboxState(pricelists, v)"
						@click.stop="() => {}"
					/>
					{{ administrationName }}
					<span class="opacity-60"> ({{ pricelists.length }}) </span>
				</v-expansion-panel-title>
				<v-expansion-panel-text
					class="overflow-y-auto"
					style="max-height: 250px"
				>
					<v-checkbox
						v-for="pricelist in pricelists"
						:key="pricelist.id"
						:model-value="selected.has(pricelist.id)"
						@update:model-value="
							(value) => setCheckboxState(pricelist.id, value)
						"
						density="compact"
						hide-details
						color="primary"
					>
						<template #label>
							<span class="ml-1 text-body-2">
								{{ pricelist.name }}
							</span>
						</template>
					</v-checkbox>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<template>
			<template v-if="Object.keys(previouslyAppliedPricelists).length > 0">
				<div class="d-flex align-center ga-2 mb-2">
					<v-icon icon="fa-regular fa-circle-check" size="14" color="primary" />
					<span
						class="text-caption font-weight-bold text-uppercase text-medium-emphasis"
					>
						Currently applied
					</span>
				</div>
				<v-sheet rounded class="px-3 pt-1 pb-2 mb-5" border>
					<template
						v-for="(
							pricelists, administrationName
						) in previouslyAppliedPricelists"
						:key="administrationName"
					>
						<p class="text-caption text-medium-emphasis mt-3 mb-1">
							{{ administrationName }}
						</p>
						<v-checkbox
							v-for="pricelist in pricelists"
							:key="pricelist.id"
							v-model="selected"
							:label="pricelist.name"
							:value="pricelist.id"
							density="compact"
							hide-details
							color="primary"
						/>
					</template>
				</v-sheet>
			</template>
		</template>

		<template #actions>
			<v-btn
				variant="text"
				color="error"
				:disabled="applying"
				@click="isVisible = false"
			>
				Cancel
			</v-btn>
			<v-btn
				color="primary"
				variant="flat"
				prepend-icon="fa-regular fa-copy"
				:disabled="applying || loading || !customName.trim()"
				:loading="applying"
				@click="apply"
			>
				Save as New Curve
			</v-btn>
		</template>
	</Modal>
</template>

<style scoped lang="scss">
.chit {
	background: #f8f8f8;
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-radius: 10px;
	box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5),
		inset 0 -1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
	height: 15px;
	width: 15px;
	display: inline-block;
}
</style>
